/**
 * TutorialContext — web port of
 * client-native/src/features/tutorial/context/TutorialContext.tsx.
 *
 * Backend-synced `completedItems` + `isDismissed`; `isCollapsed` and the
 * user-controlled `viewingPhase` are local-only (localStorage on web, mirroring
 * iOS AsyncStorage). Behavior — phase-gated viewing (cannot skip ahead), the
 * one-at-a-time celebration queue, optimistic complete/dismiss — is reproduced
 * 1:1 from the iOS provider so the checklist behaves identically.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
  FC,
} from "react";
import { usePostHog } from "posthog-js/react";
import {
  getTutorialProgress,
  completeTutorialItem as apiCompleteTutorialItem,
  dismissTutorial as apiDismissTutorial,
  undismissTutorial as apiUndismissTutorial,
} from "../api/tutorialApi";
import { AuthError } from "@/api/Api";
import {
  TutorialProgress,
  TutorialItemId,
  initialTutorialProgress,
} from "../lib/types";
import { TUTORIAL_PHASES, getTotalItemCount } from "../lib/tutorialData";

const TUTORIAL_STORAGE_KEY = "tutorialProgress";
const CELEBRATED_ITEMS_KEY = "tutorialCelebratedItems";
const VIEWING_PHASE_KEY = "tutorialViewingPhase";

// localStorage helpers (mirror iOS AsyncStorage usage).
function lsGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function lsSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private mode / quota — local persistence is best-effort.
  }
}
function lsRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* noop */
  }
}

interface TutorialContextValue {
  progress: TutorialProgress;
  visibleCompletedItems: TutorialItemId[];
  itemsNeedingCelebration: TutorialItemId[];
  isLoaded: boolean;
  isCollapsed: boolean;
  isDismissed: boolean;
  isCompleted: boolean;
  shouldShowTutorial: boolean;
  currentPhaseIndex: number;
  completedPhasesCount: number;
  progressPercentage: number;
  isCurrentPhaseComplete: boolean;
  canAdvanceToNextPhase: boolean;

  completeItem: (itemId: TutorialItemId) => void;
  completeItemWithBackend: (itemId: TutorialItemId) => Promise<boolean>;
  uncompleteItem: (itemId: TutorialItemId) => void;
  toggleItem: (itemId: TutorialItemId) => void;
  isItemCompleted: (itemId: TutorialItemId) => boolean;
  toggleCollapsed: () => void;
  setCollapsed: (collapsed: boolean) => void;
  dismissTutorial: () => Promise<void>;
  undismissTutorial: () => Promise<void>;
  resetTutorial: () => Promise<void>;
  advanceToNextPhase: () => void;
  refetch: () => Promise<void>;
  markAsCelebrated: (itemId: TutorialItemId) => void;
}

const TutorialContext = createContext<TutorialContextValue | null>(null);

/** First phase index that still has incomplete items (caps viewing phase). */
function firstIncompletePhase(completed: TutorialItemId[]): number {
  let result = 0;
  for (let i = 0; i < TUTORIAL_PHASES.length; i++) {
    const phase = TUTORIAL_PHASES[i];
    const allComplete = phase.items.every((item) =>
      completed.includes(item.id)
    );
    if (!allComplete) {
      return i;
    }
    result = Math.min(i + 1, TUTORIAL_PHASES.length - 1);
  }
  return result;
}

interface TutorialProviderProps {
  children: ReactNode;
}

export const TutorialProvider: FC<TutorialProviderProps> = ({ children }) => {
  // PostHog tutorial funnels — mirror iOS useTrackTutorialEvents (same event
  // names; platform: "web"). Fired on user-driven collapse/expand/dismiss.
  const posthog = usePostHog();

  const [progress, setProgress] = useState<TutorialProgress>(
    initialTutorialProgress
  );
  const [isLoaded, setIsLoaded] = useState(false);
  const isMountedRef = useRef(true);

  const [celebratedItems, setCelebratedItems] = useState<TutorialItemId[]>([]);
  const [viewingPhaseIndex, setViewingPhaseIndex] = useState<number>(0);
  const hasLoadedViewingPhaseRef = useRef(false);

  // Resolve viewing phase from storage, capped to the first incomplete phase
  // so users can never skip ahead.
  const resolveViewingPhase = useCallback((completed: TutorialItemId[]) => {
    if (hasLoadedViewingPhaseRef.current) return;
    const raw = lsGet(VIEWING_PHASE_KEY);
    const saved = raw ? parseInt(raw, 10) : 0;
    const capped = firstIncompletePhase(completed);
    const validated = Math.min(Number.isNaN(saved) ? 0 : saved, capped);
    if (validated !== saved) {
      lsSet(VIEWING_PHASE_KEY, validated.toString());
    }
    setViewingPhaseIndex(validated);
    hasLoadedViewingPhaseRef.current = true;
  }, []);

  const fetchProgress = useCallback(async () => {
    try {
      const backendProgress = await getTutorialProgress();

      const savedData = lsGet(TUTORIAL_STORAGE_KEY);
      const localState = savedData
        ? (JSON.parse(savedData) as TutorialProgress)
        : null;

      const celebratedData = lsGet(CELEBRATED_ITEMS_KEY);
      const rawCelebrated = celebratedData
        ? (JSON.parse(celebratedData) as TutorialItemId[])
        : [];

      // Keep only celebrated items that are ACTUALLY completed on backend.
      const savedCelebrated = rawCelebrated.filter((id) =>
        backendProgress.completedItems.includes(id)
      );
      if (savedCelebrated.length !== rawCelebrated.length) {
        lsSet(CELEBRATED_ITEMS_KEY, JSON.stringify(savedCelebrated));
      }

      if (isMountedRef.current) {
        setProgress({
          completedItems: backendProgress.completedItems,
          isDismissed: backendProgress.isDismissed,
          isCollapsed: localState?.isCollapsed ?? false,
        });
        setCelebratedItems(savedCelebrated);
        resolveViewingPhase(backendProgress.completedItems);
      }
    } catch (error) {
      if (!(error instanceof AuthError)) {
        console.error("[TutorialContext] Error fetching from backend:", error);
      }
      // Fall back to local storage.
      try {
        const savedData = lsGet(TUTORIAL_STORAGE_KEY);
        const parsed = savedData
          ? (JSON.parse(savedData) as TutorialProgress)
          : null;
        if (parsed && isMountedRef.current) {
          setProgress(parsed);
        }
        const celebratedData = lsGet(CELEBRATED_ITEMS_KEY);
        if (celebratedData && isMountedRef.current) {
          setCelebratedItems(JSON.parse(celebratedData));
        }
        resolveViewingPhase(parsed?.completedItems ?? []);
      } catch (localError) {
        console.error(
          "[TutorialContext] Error loading from local storage:",
          localError
        );
      }
    } finally {
      if (isMountedRef.current) setIsLoaded(true);
    }
  }, [resolveViewingPhase]);

  useEffect(() => {
    isMountedRef.current = true;
    void fetchProgress();
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchProgress]);

  // Persist progress to localStorage (skip the initial-load render).
  useEffect(() => {
    if (!isLoaded) return;
    lsSet(TUTORIAL_STORAGE_KEY, JSON.stringify(progress));
  }, [progress, isLoaded]);

  const completeItem = useCallback((itemId: TutorialItemId) => {
    setProgress((prev) => {
      if (prev.completedItems.includes(itemId)) return prev;
      return { ...prev, completedItems: [...prev.completedItems, itemId] };
    });
  }, []);

  const completeItemWithBackend = useCallback(
    async (itemId: TutorialItemId): Promise<boolean> => {
      try {
        await apiCompleteTutorialItem(itemId);
        completeItem(itemId);
        return true;
      } catch (error) {
        console.error("[TutorialContext] Error completing item:", error);
        // Optimistic: complete locally even if the API call fails.
        completeItem(itemId);
        return false;
      }
    },
    [completeItem]
  );

  const uncompleteItem = useCallback((itemId: TutorialItemId) => {
    setProgress((prev) => ({
      ...prev,
      completedItems: prev.completedItems.filter((id) => id !== itemId),
    }));
  }, []);

  const toggleItem = useCallback((itemId: TutorialItemId) => {
    setProgress((prev) => {
      if (prev.completedItems.includes(itemId)) {
        return {
          ...prev,
          completedItems: prev.completedItems.filter((id) => id !== itemId),
        };
      }
      return { ...prev, completedItems: [...prev.completedItems, itemId] };
    });
  }, []);

  const isItemCompleted = useCallback(
    (itemId: TutorialItemId): boolean =>
      progress.completedItems.includes(itemId),
    [progress.completedItems]
  );

  const toggleCollapsed = useCallback(() => {
    const nextCollapsed = !progress.isCollapsed;
    if (nextCollapsed) {
      posthog.capture("tutorial_collapsed", { platform: "web" });
    } else {
      posthog.capture("tutorial_expanded", { platform: "web" });
    }
    setProgress((prev) => ({ ...prev, isCollapsed: nextCollapsed }));
  }, [posthog, progress.isCollapsed]);

  const setCollapsed = useCallback((collapsed: boolean) => {
    setProgress((prev) => ({ ...prev, isCollapsed: collapsed }));
  }, []);

  const dismissTutorial = useCallback(async () => {
    posthog.capture("tutorial_dismissed", { platform: "web" });
    try {
      await apiDismissTutorial();
    } catch (error) {
      console.error("[TutorialContext] Error dismissing tutorial:", error);
    }
    setProgress((prev) => ({ ...prev, isDismissed: true }));
  }, [posthog]);

  const undismissTutorial = useCallback(async () => {
    try {
      await apiUndismissTutorial();
    } catch (error) {
      console.error("[TutorialContext] Error undismissing tutorial:", error);
    }
    setProgress((prev) => ({ ...prev, isDismissed: false }));
  }, []);

  const currentPhaseIndex = viewingPhaseIndex;

  const isCurrentPhaseComplete = useMemo(() => {
    if (currentPhaseIndex >= TUTORIAL_PHASES.length) return true;
    const phase = TUTORIAL_PHASES[currentPhaseIndex];
    return phase.items.every((item) =>
      progress.completedItems.includes(item.id)
    );
  }, [currentPhaseIndex, progress.completedItems]);

  const canAdvanceToNextPhase = useMemo(
    () =>
      isCurrentPhaseComplete &&
      currentPhaseIndex < TUTORIAL_PHASES.length - 1,
    [isCurrentPhaseComplete, currentPhaseIndex]
  );

  const advanceToNextPhase = useCallback(() => {
    if (!isCurrentPhaseComplete) return;
    if (currentPhaseIndex >= TUTORIAL_PHASES.length - 1) return;
    const next = currentPhaseIndex + 1;
    setViewingPhaseIndex(next);
    lsSet(VIEWING_PHASE_KEY, next.toString());
  }, [isCurrentPhaseComplete, currentPhaseIndex]);

  // Completed items belonging to the current + prior phases (future-phase
  // completions stay hidden until the user reaches that phase).
  const visibleCompletedItems = useMemo(() => {
    const visibleIds = new Set<TutorialItemId>();
    for (let i = 0; i <= currentPhaseIndex; i++) {
      TUTORIAL_PHASES[i]?.items.forEach((item) => visibleIds.add(item.id));
    }
    return progress.completedItems.filter((id) => visibleIds.has(id));
  }, [progress.completedItems, currentPhaseIndex]);

  // Completed-but-not-yet-celebrated items in the CURRENT phase (the only ones
  // that get confetti; prior-phase items are auto-marked celebrated below).
  const itemsNeedingCelebration = useMemo(() => {
    const currentPhaseIds = new Set<TutorialItemId>();
    TUTORIAL_PHASES[currentPhaseIndex]?.items.forEach((item) =>
      currentPhaseIds.add(item.id)
    );
    return visibleCompletedItems.filter(
      (id) => !celebratedItems.includes(id) && currentPhaseIds.has(id)
    );
  }, [visibleCompletedItems, celebratedItems, currentPhaseIndex]);

  const markAsCelebrated = useCallback((itemId: TutorialItemId) => {
    setCelebratedItems((prev) => {
      if (prev.includes(itemId)) return prev;
      const updated = [...prev, itemId];
      lsSet(CELEBRATED_ITEMS_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Auto-mark items from PRIOR phases as celebrated (we missed their window).
  useEffect(() => {
    const previousPhaseIds = new Set<TutorialItemId>();
    for (let i = 0; i < currentPhaseIndex; i++) {
      TUTORIAL_PHASES[i]?.items.forEach((item) => previousPhaseIds.add(item.id));
    }
    const uncelebratedPrevious = visibleCompletedItems.filter(
      (id) => previousPhaseIds.has(id) && !celebratedItems.includes(id)
    );
    if (uncelebratedPrevious.length > 0) {
      setCelebratedItems((prev) => {
        const updated = [...new Set([...prev, ...uncelebratedPrevious])];
        lsSet(CELEBRATED_ITEMS_KEY, JSON.stringify(updated));
        return updated;
      });
    }
  }, [currentPhaseIndex, visibleCompletedItems, celebratedItems]);

  const completedPhasesCount = useMemo(() => {
    let count = 0;
    for (const phase of TUTORIAL_PHASES) {
      const allComplete = phase.items.every((item) =>
        visibleCompletedItems.includes(item.id)
      );
      if (allComplete) count++;
      else break;
    }
    return count;
  }, [visibleCompletedItems]);

  const progressPercentage = useMemo(() => {
    const total = getTotalItemCount();
    if (total === 0) return 0;
    return Math.round((visibleCompletedItems.length / total) * 100);
  }, [visibleCompletedItems]);

  const isCompleted = useMemo(
    () => visibleCompletedItems.length >= getTotalItemCount(),
    [visibleCompletedItems]
  );

  // Keep showing the tutorial even when complete (until dismissed) so the
  // completion celebration can play.
  const shouldShowTutorial = useMemo(
    () => !progress.isDismissed,
    [progress.isDismissed]
  );

  const resetTutorial = useCallback(async () => {
    setProgress(initialTutorialProgress);
    setCelebratedItems([]);
    setViewingPhaseIndex(0);
    hasLoadedViewingPhaseRef.current = false;
    lsRemove(TUTORIAL_STORAGE_KEY);
    lsRemove(CELEBRATED_ITEMS_KEY);
    lsRemove(VIEWING_PHASE_KEY);
  }, []);

  const value: TutorialContextValue = {
    progress,
    visibleCompletedItems,
    itemsNeedingCelebration,
    isLoaded,
    isCollapsed: progress.isCollapsed,
    isDismissed: progress.isDismissed,
    isCompleted,
    shouldShowTutorial,
    currentPhaseIndex,
    completedPhasesCount,
    progressPercentage,
    isCurrentPhaseComplete,
    canAdvanceToNextPhase,

    completeItem,
    completeItemWithBackend,
    uncompleteItem,
    toggleItem,
    isItemCompleted,
    toggleCollapsed,
    setCollapsed,
    dismissTutorial,
    undismissTutorial,
    resetTutorial,
    advanceToNextPhase,
    refetch: fetchProgress,
    markAsCelebrated,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTutorial = (): TutorialContextValue => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error("useTutorial must be used within a TutorialProvider");
  }
  return context;
};
